/**
 * 协议签署状态检查 Hook
 * 用于检查用户是否需要签署协议
 */

import { useCallback, useEffect, useState } from "react";
import { useConsentStore } from "@/stores/consentStore";
import { useUserStore } from "@/stores/userStore";
import { agreementService } from "@/services/agreementService";
import { useOperationRegion } from "@/hooks/useOperationRegion";

/** 缓存有效期：5 分钟 */
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * 根据运营区域获取协议区域代码
 * domestic -> CN
 * international -> GLOBAL
 */
function getRegionCode(operationRegion: "domestic" | "international"): string {
  return operationRegion === "domestic" ? "CN" : "GLOBAL";
}

/**
 * 协议签署状态检查 Hook
 *
 * 功能：
 * - 检查用户是否需要签署协议
 * - 实现 5 分钟缓存机制
 * - 自动在登录后检查
 * - 错误降级处理
 * - 根据运营区域获取对应区域的协议
 */
export function useConsentCheck() {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const { region: operationRegion, loading: regionLoading } =
    useOperationRegion();
  const [checkError, setCheckError] = useState<string | null>(null);
  const {
    needsReconsent,
    pendingAgreements,
    isChecking,
    lastCheckedAt,
    setPendingAgreements,
    setNeedsReconsent,
    setIsChecking,
    setLastCheckedAt,
    openModal,
  } = useConsentStore();

  /**
   * 检查签署状态
   * @param force 是否强制检查（忽略缓存）
   */
  const checkConsent = useCallback(
    async (force = false) => {
      if (!isAuthenticated || regionLoading) return;

      // 检查缓存是否有效
      if (
        !force &&
        lastCheckedAt &&
        Date.now() - lastCheckedAt < CACHE_DURATION
      ) {
        return;
      }

      setIsChecking(true);
      setCheckError(null);

      try {
        // 根据运营区域获取对应的协议区域代码
        const regionCode = getRegionCode(operationRegion);
        const result = await agreementService.checkConsentStatus(regionCode);
        setNeedsReconsent(result.needs_reconsent);
        setPendingAgreements(result.agreements || []);
        setLastCheckedAt(Date.now());

        if (result.needs_reconsent) {
          openModal();
        }
      } catch (error) {
        // 记录错误日志
        console.error("检查签署状态失败:", error);

        // 设置错误状态
        const message =
          error instanceof Error ? error.message : "检查签署状态失败";
        setCheckError(message);

        // 降级处理：允许用户继续使用，但记录错误
        // 不阻断用户操作，下次会重新检查
      } finally {
        setIsChecking(false);
      }
    },
    [
      isAuthenticated,
      regionLoading,
      operationRegion,
      lastCheckedAt,
      setPendingAgreements,
      setNeedsReconsent,
      setIsChecking,
      setLastCheckedAt,
      openModal,
    ]
  );

  // 登录后自动检查（等待运营区域加载完成）
  useEffect(() => {
    if (isAuthenticated && !regionLoading) {
      checkConsent();
    }
  }, [isAuthenticated, regionLoading, checkConsent]);

  return {
    needsReconsent,
    pendingAgreements,
    isChecking,
    checkConsent,
    checkError,
    hasForceUpdate: pendingAgreements.some((a) => a.force_update),
  };
}

export default useConsentCheck;
